<template>
    <ui-modal
        :is-open="show"
        @close="closeModal"
        :max-width="{ 'max-width': '700px' }"
    >
        <template #title>
            <header class="h-[52px] flex items-center gap-3">
                <div class="flex-1 pr-12">
                    <div class="flex justify-between items-center mb-1">
                        <h1
                            class="text-slate-800 text-xl font-medium font-['Inter'] leading-7"
                        >
                            Copy card details to another card
                        </h1>
                        <div class="w-5 h-5 p-2 rounded overflow-hidden"></div>
                    </div>
                    <p
                        class="text-slate-500 text-sm font-normal font-['Inter'] leading-tight"
                    >
                        {{ subheaderText }}
                    </p>
                </div>
            </header>
        </template>
        <template #content>
            <!-- Step 1: Select receiving card -->
            <section
                v-if="currentStep === 1 || currentStep === 2"
                class="step transition-all duration-300 py-2 lg:min-h-[500px] flex flex-col"
            >
                <div class="relative flex flex-row gap-3 flex-1">
                    <!-- Source card -->
                    <article
                        class="w-1/2 rounded-lg border border-slate-200 bg-white px-6 py-4"
                    >
                        <div class="mb-3">
                            <label for="credit-card-dropdown">From</label>
                            <WalletCardDropdown
                                id="credit-card-dropdown"
                                v-model="selectedCard"
                                label="From"
                                :exclude-card="selectedReceivingCard"
                                @update:model-value="setCardToCopy"
                                :disabled="
                                    currentStep === 2 || currentStep === 3
                                "
                            />
                            <Errors
                                :errors="errors['selectedCard']"
                                class="pt-2"
                            />
                        </div>
                        <!-- Step 2: Select details -->
                        <TransitionGroup name="fade">
                            <div
                                v-if="currentStep === 2 && selectedCard"
                                class="mb-2 md:mb-5"
                                key="details"
                            >
                                <header
                                    class="flex justify-between items-center pb-2"
                                >
                                    <h2
                                        class="text-slate-600 text-sm font-medium font-['Inter'] leading-tight"
                                    >
                                        Details to copy
                                    </h2>
                                    <label class="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            class="form-checkbox"
                                            v-model="selectAll"
                                            @change="toggleSelectAll"
                                        />
                                        <span
                                            class="whitespace-nowrap text-slate-800 text-sm font-normal font-['Inter'] leading-tight"
                                        >
                                            Select all
                                        </span>
                                    </label>
                                </header>
                                <Errors
                                    :errors="errors['madeSelections']"
                                    class="pt-2"
                                />
                                <ul
                                    v-if="selectedCard"
                                    class="details-list transition-all"
                                >
                                    <li
                                        v-for="(detail, i) in copyableDetails"
                                        :key="`source-${detail.key}`"
                                        class="border-slate-200"
                                        :class="{
                                            'border-b':
                                                i < copyableDetails.length - 1,
                                        }"
                                    >
                                        <div class="h-14 py-2">
                                            <label
                                                class="flex items-center gap-3 h-full"
                                            >
                                                <input
                                                    type="checkbox"
                                                    class="form-checkbox"
                                                    v-model="
                                                        selectedDetails[
                                                            detail.key
                                                        ]
                                                    "
                                                    @change="onDetailToggled"
                                                />
                                                <div
                                                    class="flex-1 flex flex-col justify-center"
                                                >
                                                    <span
                                                        class="text-slate-500 text-sm font-normal font-['Inter'] leading-tight"
                                                    >
                                                        {{ detail.label }}
                                                    </span>

                                                    <span
                                                        class="text-slate-800 text-sm font-normal font-['Inter'] leading-tight"
                                                    >
                                                        {{
                                                            formatCardDetails(
                                                                selectedCard[
                                                                    detail.key
                                                                ],
                                                                detail.key
                                                            )
                                                        }}
                                                    </span>
                                                </div>
                                            </label>
                                        </div>
                                        <!-- Welcome bonus sub-details -->
                                        <Transition name="expand">
                                            <ul
                                                v-if="
                                                    detail.key ===
                                                        'bonus_received' &&
                                                    selectedDetails[
                                                        detail.key
                                                    ] &&
                                                    selectedCard?.bonus_received
                                                "
                                                class="pb-4 -mt-1 -ml-1.5 pl-3"
                                            >
                                                <li
                                                    v-for="(
                                                        subDetail, i
                                                    ) in welcomeBonusDetails"
                                                    :key="subDetail.key"
                                                    class="relative flex flex-col gap-1 pt-2 border-l pl-4 h-[52px]"
                                                    :class="
                                                        i <
                                                        welcomeBonusDetails.length -
                                                            1
                                                            ? 'border-slate-300'
                                                            : 'border-transparent'
                                                    "
                                                >
                                                    <svg
                                                        width="12"
                                                        height="40"
                                                        viewBox="0 0 12 40"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="absolute -left-0.5"
                                                        :class="
                                                            i <
                                                            welcomeBonusDetails.length -
                                                                1
                                                                ? 'top-0'
                                                                : '-top-px'
                                                        "
                                                    >
                                                        <path
                                                            id="vector"
                                                            d="M1.46875 1V31C1.46875 35.4183 5.05047 39 9.46875 39H11.4688"
                                                            stroke="#CBD5E1"
                                                            stroke-linecap="round"
                                                        />
                                                    </svg>
                                                    <span
                                                        class="text-slate-600 text-sm font-normal"
                                                    >
                                                        {{ subDetail.label }}
                                                    </span>
                                                    <span
                                                        class="text-slate-800 text-sm font-medium"
                                                    >
                                                        {{
                                                            formatCardDetails(
                                                                selectedCard[
                                                                    subDetail
                                                                        .key
                                                                ],
                                                                subDetail.key
                                                            )
                                                        }}
                                                    </span>
                                                </li>
                                            </ul>
                                        </Transition>

                                        <!-- "From" card: Product changed sub-details -->
                                        <Transition name="expand">
                                            <ul
                                                v-if="
                                                    detail.key ===
                                                        'product_changed' &&
                                                    selectedDetails[
                                                        detail.key
                                                    ] &&
                                                    selectedCard?.product_changed
                                                "
                                                class="pb-4 -mt-1 -ml-1.5 pl-3"
                                            >
                                                <li
                                                    v-for="(
                                                        subDetail, i
                                                    ) in productChangedDetails"
                                                    :key="subDetail.key"
                                                    class="relative flex flex-col gap-1 pt-2 border-l pl-4 h-[52px]"
                                                    :class="
                                                        i <
                                                        productChangedDetails.length -
                                                            1
                                                            ? 'border-slate-300'
                                                            : 'border-transparent'
                                                    "
                                                >
                                                    <svg
                                                        width="12"
                                                        height="40"
                                                        viewBox="0 0 12 40"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="absolute -left-0.5"
                                                        :class="
                                                            i <
                                                            productChangedDetails.length -
                                                                1
                                                                ? 'top-0'
                                                                : '-top-px'
                                                        "
                                                    >
                                                        <path
                                                            id="vector"
                                                            d="M1.46875 1V31C1.46875 35.4183 5.05047 39 9.46875 39H11.4688"
                                                            stroke="#CBD5E1"
                                                            stroke-linecap="round"
                                                        />
                                                    </svg>
                                                    <span
                                                        class="text-slate-600 text-sm font-normal"
                                                    >
                                                        {{ subDetail.label }}
                                                    </span>
                                                    <span
                                                        class="text-slate-800 text-sm font-normal font-['Inter'] leading-tight"
                                                    >
                                                        <span
                                                            class="text-slate-800 text-sm font-normal font-['Inter'] leading-tight"
                                                        >
                                                            {{
                                                                subDetail.key ===
                                                                'original_card'
                                                                    ? selectedCard
                                                                          ?.original_card
                                                                          ?.title
                                                                    : formatCardDetails(
                                                                          selectedCard[
                                                                              subDetail
                                                                                  .key
                                                                          ],
                                                                          subDetail.key
                                                                      )
                                                            }}
                                                        </span>
                                                    </span>
                                                </li>
                                            </ul>
                                        </Transition>
                                    </li>
                                </ul>
                            </div>
                            <div
                                v-else-if="!selectedCard"
                                class="pt-6 pb-16 flex justify-center items-center flex-col"
                                key="noDetails"
                            >
                                <Icon icon="credit-cards" />
                                <div
                                    class="pt-2 flex-col justify-start items-center gap-1 flex text-slate-500 text-sm font-normal font-['Inter'] leading-tight"
                                >
                                    Select a card to begin.
                                </div>
                            </div>
                        </TransitionGroup>
                    </article>
                    <!-- ARROW ICON -->
                    <div
                        class="absolute left-1/2 top-12 -translate-x-1/2 h-10 p-2.5 bg-white rounded-full shadow-[0px_0px_0px_2px_rgba(255,255,255,1.00)] border border-slate-200 justify-center items-center inline-flex overflow-hidden"
                    >
                        <div
                            class="w-5 h-5 justify-center items-center flex overflow-hidden"
                        >
                            <div
                                class="w-5 h-5 relative flex-col justify-start items-start flex overflow-hidden"
                            >
                                <Icon icon="arrow-right-alt" />
                            </div>
                        </div>
                    </div>
                    <!-- Receiving card -->
                    <article
                        class="w-1/2 rounded-lg border border-slate-200 bg-slate-50 px-6 py-4"
                    >
                        <div class="mb-3">
                            <label for="receiving-card-dropdown">To</label>
                            <WalletCardDropdown
                                id="receiving-card-dropdown"
                                v-model="selectedReceivingCard"
                                label="To"
                                :exclude-card="selectedCard"
                                @update:model-value="setReceivingCard"
                                :disabled="
                                    currentStep === 2 || currentStep === 3
                                "
                            />
                            <Errors
                                :errors="errors['receivingCard']"
                                class="pt-2"
                            />
                        </div>
                        <!-- Step 2: Select details -->
                        <TransitionGroup name="fade">
                            <div
                                v-if="currentStep === 1"
                                class="pt-6 pb-16 flex justify-center items-center flex-col h-full"
                                key="receiving-step1"
                            >
                                <div
                                    class="w-6 h-6 justify-center items-center flex overflow-hidden"
                                >
                                    <Icon icon="credit-cards" />
                                </div>
                                <div
                                    class="pt-2 flex-col justify-start items-center gap-1 flex text-slate-500 text-sm font-normal font-['Inter'] leading-tight"
                                >
                                    Select a card to begin.
                                </div>
                            </div>
                            <div
                                v-if="currentStep === 2"
                                class="mb-2 md:mb-5 h-full flex flex-col"
                                key="receiving-step2"
                            >
                                <header
                                    class="flex justify-between items-center pb-2"
                                >
                                    <h2
                                        class="text-slate-600 text-sm font-medium font-['Inter'] leading-tight"
                                    >
                                        Result
                                    </h2>
                                </header>

                                <TransitionGroup
                                    tag="ul"
                                    name="list"
                                    class="details-list"
                                >
                                    <li
                                        v-for="(detail, i) in copyableDetails"
                                        :key="`receiving-${detail.key}`"
                                        class="border-slate-200 transition-all duration-300 ease-in-out"
                                        :class="{
                                            'border-b':
                                                i < copyableDetails.length - 1,
                                        }"
                                    >
                                        <div class="h-14 py-2">
                                            <div
                                                v-if="isSelected(detail.key)"
                                                class="flex items-center gap-3 h-full transition-opacity duration-300"
                                            >
                                                <div
                                                    class="flex-1 flex flex-col justify-center"
                                                >
                                                    <span
                                                        class="text-slate-500 text-sm font-normal font-['Inter'] leading-tight"
                                                    >
                                                        {{ detail.label }}
                                                    </span>
                                                    <div
                                                        class="flex items-center gap-1"
                                                    >
                                                        <span
                                                            v-if="
                                                                detail.key ===
                                                                    'annual_fee' &&
                                                                !selectedReceivingCard.user_managed_annual_fee
                                                            "
                                                            class="line-through text-slate-800 text-sm font-normal font-['Inter'] leading-tight"
                                                        >
                                                            {{
                                                                formatCardDetails(
                                                                    selectedReceivingCard
                                                                        .card
                                                                        .annual_fee,
                                                                    detail.key
                                                                )
                                                            }}
                                                        </span>
                                                        <span
                                                            v-else
                                                            class="line-through text-slate-800 text-sm font-normal font-['Inter'] leading-tight"
                                                        >
                                                            {{
                                                                formatCardDetails(
                                                                    selectedReceivingCard[
                                                                        detail
                                                                            .key
                                                                    ],
                                                                    detail.key
                                                                )
                                                            }}
                                                        </span>

                                                        <svg
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 16 16"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            class="flex-shrink-0"
                                                        >
                                                            <path
                                                                d="M2.66675 8.00008H13.3334M13.3334 8.00008L9.33341 4.00008M13.3334 8.00008L9.33341 12.0001"
                                                                stroke="#64748B"
                                                                stroke-width="1.33333"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                            />
                                                        </svg>

                                                        <span
                                                            class="text-slate-800 text-sm font-normal font-['Inter'] leading-tight"
                                                        >
                                                            {{
                                                                formatCardDetails(
                                                                    selectedCard[
                                                                        detail
                                                                            .key
                                                                    ],
                                                                    detail.key
                                                                )
                                                            }}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <!-- Receiving card: Welcome bonus sub-details -->
                                        <Transition name="expand">
                                            <ul
                                                v-if="
                                                    detail.key ===
                                                        'bonus_received' &&
                                                    selectedDetails[
                                                        detail.key
                                                    ] &&
                                                    selectedCard?.bonus_received
                                                "
                                                class="pb-4 -mt-1 -ml-1.5 pl-3 transition-all duration-300 ease-in-out"
                                            >
                                                <li
                                                    v-for="(
                                                        subDetail, i
                                                    ) in welcomeBonusDetails"
                                                    :key="subDetail.key"
                                                    class="relative flex flex-col gap-1 pt-2 border-l pl-4 h-[52px] transition-all duration-500"
                                                    :class="
                                                        i <
                                                        welcomeBonusDetails.length -
                                                            1
                                                            ? 'border-slate-300'
                                                            : 'border-transparent'
                                                    "
                                                >
                                                    <svg
                                                        width="12"
                                                        height="40"
                                                        viewBox="0 0 12 40"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="absolute -left-0.5"
                                                        :class="
                                                            i <
                                                            welcomeBonusDetails.length -
                                                                1
                                                                ? 'top-0'
                                                                : '-top-px'
                                                        "
                                                    >
                                                        <path
                                                            id="vector"
                                                            d="M1.46875 1V31C1.46875 35.4183 5.05047 39 9.46875 39H11.4688"
                                                            stroke="#CBD5E1"
                                                            stroke-linecap="round"
                                                        />
                                                    </svg>
                                                    <div
                                                        class="text-slate-600 text-sm font-normal"
                                                    >
                                                        {{ subDetail.label }}
                                                    </div>
                                                    <div
                                                        class="flex items-center gap-1"
                                                    >
                                                        <span
                                                            class="line-through text-slate-800 text-sm font-normal font-['Inter'] leading-tight"
                                                        >
                                                            {{
                                                                formatCardDetails(
                                                                    selectedReceivingCard[
                                                                        subDetail
                                                                            .key
                                                                    ],
                                                                    subDetail.key
                                                                )
                                                            }}
                                                        </span>
                                                        <svg
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 16 16"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            class="flex-shrink-0"
                                                        >
                                                            <path
                                                                d="M2.66675 8.00008H13.3334M13.3334 8.00008L9.33341 4.00008M13.3334 8.00008L9.33341 12.0001"
                                                                stroke="#64748B"
                                                                stroke-width="1.33333"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                            />
                                                        </svg>
                                                        <div
                                                            class="text-slate-800 text-sm font-normal font-['Inter'] leading-tight"
                                                        >
                                                            {{
                                                                formatCardDetails(
                                                                    selectedCard[
                                                                        subDetail
                                                                            .key
                                                                    ],
                                                                    subDetail.key
                                                                )
                                                            }}
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </Transition>

                                        <!-- Receiving card: Product changed sub-details -->
                                        <Transition name="expand">
                                            <ul
                                                v-if="
                                                    detail.key ===
                                                        'product_changed' &&
                                                    selectedDetails[
                                                        detail.key
                                                    ] &&
                                                    selectedCard?.product_changed
                                                "
                                                class="pb-4 -mt-1 -ml-1.5 pl-3 transition-all duration-300 ease-in-out"
                                            >
                                                <li
                                                    v-for="(
                                                        subDetail, i
                                                    ) in productChangedDetails"
                                                    :key="subDetail.key"
                                                    class="relative flex flex-col gap-1 pt-2 border-l pl-4 h-[52px] transition-all duration-300 ease-in-out"
                                                    :class="
                                                        i <
                                                        productChangedDetails.length -
                                                            1
                                                            ? 'border-slate-300'
                                                            : 'border-transparent'
                                                    "
                                                >
                                                    <svg
                                                        width="12"
                                                        height="40"
                                                        viewBox="0 0 12 40"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="absolute -left-0.5"
                                                        :class="
                                                            i <
                                                            productChangedDetails.length -
                                                                1
                                                                ? 'top-0'
                                                                : '-top-px'
                                                        "
                                                    >
                                                        <path
                                                            id="vector"
                                                            d="M1.46875 1V31C1.46875 35.4183 5.05047 39 9.46875 39H11.4688"
                                                            stroke="#CBD5E1"
                                                            stroke-linecap="round"
                                                        />
                                                    </svg>
                                                    <div
                                                        class="text-slate-600 text-sm font-normal"
                                                    >
                                                        {{ subDetail.label }}
                                                    </div>
                                                    <div
                                                        class="flex items-center gap-1"
                                                    >
                                                        <div
                                                            class="text-slate-800 text-sm font-normal font-['Inter'] leading-tight"
                                                        >
                                                            {{
                                                                subDetail.key ===
                                                                'original_card'
                                                                    ? selectedCard
                                                                          ?.original_card
                                                                          ?.title
                                                                    : formatCardDetails(
                                                                          selectedCard[
                                                                              subDetail
                                                                                  .key
                                                                          ],
                                                                          subDetail.key
                                                                      )
                                                            }}
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </Transition>
                                    </li>
                                </TransitionGroup>
                            </div>
                        </TransitionGroup>
                    </article>
                </div>
            </section>

            <!-- Step 3: Confirm -->
            <section
                v-if="currentStep === 3"
                class="step transition-all duration-300 py-2 lg:min-h-[500px] flex flex-col"
            >
                <article
                    class="mb-2 md:mb-5 px-6 pt-6 pb-4 bg-slate-50 rounded-lg border border-slate-200"
                >
                    <header class="h-16 pb-4 flex items-center gap-3">
                        <div class="rounded-sm overflow-hidden">
                            <ImageLoader>
                                <template #image>
                                    <img
                                        :src="`${cdnUrl}/${selectedReceivingCard?.card?.card_image?.path}/_medium/${selectedReceivingCard?.card?.card_image?.file_name}`"
                                        class="h-12 rounded-sm"
                                    />
                                </template>
                                <template #preloader>
                                    <div
                                        class="h-12 w-12 rounded-sm bg-slate-100 shimmer"
                                    ></div>
                                </template>
                                <template #error>
                                    <Icon icon="credit-card" class="w-8 h-5" />
                                </template>
                            </ImageLoader>
                        </div>
                        <h2
                            class="text-slate-800 text-sm font-medium font-['Inter'] leading-tight"
                        >
                            {{ selectedReceivingCard?.card?.title }}
                            {{
                                selectedReceivingCard?.last_four
                                    ? `(${selectedReceivingCard?.last_four})`
                                    : ''
                            }}
                        </h2>
                    </header>
                    <h3
                        class="flex items-start justify-start text-slate-600 text-sm font-medium"
                    >
                        Details to copy or override
                    </h3>
                    <div class="selected-details bg-gray-50 pt-4 rounded-lg">
                        <ul class="flex flex-col">
                            <li
                                v-for="(detail, i) in copyableDetails"
                                :key="detail"
                                class="min-h-11 py-3 border-slate-200"
                                :class="{
                                    'border-b': i < copyableDetails.length - 1,
                                }"
                            >
                                <div class="flex flex-col w-full">
                                    <div
                                        class="flex justify-between items-center"
                                        :class="{
                                            'opacity-50': !isSelected(
                                                detail.key
                                            ),
                                        }"
                                    >
                                        <span
                                            class="text-slate-500 text-sm font-normal font-['Inter'] leading-tight"
                                        >
                                            {{ detail.label }}
                                        </span>
                                        <div
                                            class="justify-start items-center gap-1 flex"
                                        >
                                            <div
                                                class="text-slate-800 text-sm font-normal font-['Inter'] leading-tight flex gap-1"
                                            >
                                                <span
                                                    v-if="
                                                        detail.key ===
                                                            'annual_fee' &&
                                                        !selectedReceivingCard.user_managed_annual_fee
                                                    "
                                                    :class="{
                                                        'line-through':
                                                            isSelected(
                                                                detail.key
                                                            ),
                                                    }"
                                                >
                                                    {{
                                                        formatCardDetails(
                                                            selectedReceivingCard
                                                                .card
                                                                .annual_fee,
                                                            detail.key
                                                        )
                                                    }}
                                                </span>
                                                <span
                                                    v-else
                                                    :class="{
                                                        'line-through':
                                                            isSelected(
                                                                detail.key
                                                            ),
                                                    }"
                                                >
                                                    {{
                                                        formatCardDetails(
                                                            selectedReceivingCard[
                                                                detail.key
                                                            ],
                                                            detail.key
                                                        )
                                                    }}
                                                </span>
                                                <span
                                                    v-if="
                                                        isSelected(detail.key)
                                                    "
                                                    class="flex gap-1 transition-all"
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="flex-shrink-0"
                                                    >
                                                        <path
                                                            d="M2.66675 8.00008H13.3334M13.3334 8.00008L9.33341 4.00008M13.3334 8.00008L9.33341 12.0001"
                                                            stroke="#64748B"
                                                            stroke-width="1.33333"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                        />
                                                    </svg>
                                                    <span>
                                                        {{
                                                            formatCardDetails(
                                                                selectedCard[
                                                                    detail.key
                                                                ],
                                                                detail.key
                                                            )
                                                        }}
                                                    </span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Confirmation: Welcome bonus sub-details -->
                                    <Transition name="expand">
                                        <ul
                                            v-if="
                                                detail.key ===
                                                    'bonus_received' &&
                                                selectedDetails[detail.key] &&
                                                selectedCard?.bonus_received
                                            "
                                            class="pb-3 pl-3 -ml-1.5 mt-3 overflow-hidden transition-all"
                                        >
                                            <li
                                                v-for="(
                                                    subDetail, i
                                                ) in welcomeBonusDetails"
                                                :key="subDetail.key"
                                                class="relative flex justify-between pt-3 border-l pl-4"
                                                :class="
                                                    i <
                                                    welcomeBonusDetails.length -
                                                        1
                                                        ? 'border-slate-300'
                                                        : 'border-transparent'
                                                "
                                            >
                                                <svg
                                                    width="12"
                                                    height="40"
                                                    viewBox="0 0 12 40"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    class="absolute -left-0.5 -top-1/2"
                                                >
                                                    <path
                                                        id="vector"
                                                        d="M1.46875 1V31C1.46875 35.4183 5.05047 39 9.46875 39H11.4688"
                                                        stroke="#CBD5E1"
                                                        stroke-linecap="round"
                                                    />
                                                </svg>
                                                <p
                                                    class="text-slate-600 text-sm font-normal"
                                                >
                                                    {{ subDetail.label }}
                                                </p>
                                                <p
                                                    class="text-slate-800 text-sm font-medium"
                                                >
                                                    {{
                                                        formatCardDetails(
                                                            selectedCard[
                                                                subDetail.key
                                                            ],
                                                            subDetail.key
                                                        )
                                                    }}
                                                </p>
                                            </li>
                                        </ul>
                                    </Transition>

                                    <!-- Confirmation: Product changed sub-details -->
                                    <Transition name="expand">
                                        <ul
                                            v-if="
                                                detail.key ===
                                                    'product_changed' &&
                                                selectedDetails[detail.key] &&
                                                selectedCard?.product_changed
                                            "
                                            class="pb-3 pl-3 -ml-1.5 mt-3 overflow-hidden transition-all"
                                        >
                                            <li
                                                v-for="(
                                                    subDetail, i
                                                ) in productChangedDetails"
                                                :key="subDetail.key"
                                                class="relative flex justify-between pt-3 border-l pl-4"
                                                :class="
                                                    i <
                                                    productChangedDetails.length -
                                                        1
                                                        ? 'border-slate-300'
                                                        : 'border-transparent'
                                                "
                                            >
                                                <svg
                                                    width="12"
                                                    height="40"
                                                    viewBox="0 0 12 40"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    class="absolute -left-0.5 -top-1/2"
                                                >
                                                    <path
                                                        id="vector"
                                                        d="M1.46875 1V31C1.46875 35.4183 5.05047 39 9.46875 39H11.4688"
                                                        stroke="#CBD5E1"
                                                        stroke-linecap="round"
                                                    />
                                                </svg>
                                                <p
                                                    class="text-slate-600 text-sm font-normal"
                                                >
                                                    {{ subDetail.label }}
                                                </p>
                                                <p
                                                    class="text-slate-800 text-sm font-medium"
                                                >
                                                    {{
                                                        subDetail.key ===
                                                        'original_card'
                                                            ? selectedCard
                                                                  ?.original_card
                                                                  ?.title
                                                            : formatCardDetails(
                                                                  selectedCard[
                                                                      subDetail
                                                                          .key
                                                                  ],
                                                                  subDetail.key
                                                              )
                                                    }}
                                                </p>
                                            </li>
                                        </ul>
                                    </Transition>
                                </div>
                            </li>
                        </ul>
                    </div>
                </article>
                <label class="flex items-center ml-1">
                    <input
                        type="checkbox"
                        class="form-checkbox"
                        v-model="confirmChanges"
                    />
                    <span class="ml-2 text-sm">
                        I understand these changes cannot be undone
                    </span>
                </label>
                <Errors :errors="errors['confirmChanges']" class="pt-2 pl-0" />
            </section>
        </template>

        <template #footer>
            <div class="relative justify-end items-center flex gap-4">
                <StepIndicator
                    :current-step="currentStep"
                    :total-steps="3"
                    class="absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-1/2"
                />
                <div class="justify-start items-start gap-2 flex">
                    <button
                        class="btn-base btn-white"
                        @click="prevStepOrCancel"
                        id="hs-wallet-copy-card-details-back"
                    >
                        {{ currentStep === 1 ? 'Cancel' : 'Back' }}
                    </button>
                    <button
                        class="btn-base btn-dark"
                        @click="nextStep"
                        id="hs-wallet-copy-card-details-next"
                    >
                        {{ currentStep < 3 ? 'Next' : 'Copy Details' }}
                    </button>
                </div>
            </div>
        </template>
    </ui-modal>
</template>

<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useWalletStore } from '@/store/wallet';
import { notify } from 'notiwind';
import Errors from '@/components/errors.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Filters from '@/filters';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import WalletCardDropdown from '@/components/wallet/wallet-card-dropdown.vue';
import StepIndicator from '@/components/ui/step-indicator/step-indicator.vue';
import {
    COPYABLE_DETAILS,
    WELCOME_BONUS_DETAILS,
    PRODUCT_CHANGED_DETAILS,
} from '@/components/wallet/constants/card-details';

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close']);

const walletStore = useWalletStore();
const { updateCard, setSelectedCard } = walletStore;
const { selectedCard } = storeToRefs(walletStore);
const cdnUrl = computed(() => window.cdnUrl);

const currentStep = ref(1);
const selectedReceivingCard = ref(null);
const selectAll = ref(false);
const confirmChanges = ref(false);
const errors = ref({
    receivingCard: [],
    selectedCard: [],
    madeSelections: [],
    confirmChanges: [],
});
const selectedDetails = ref({
    card_status: false,
    date_opened: false,
    account_type: false,
    credit_limit: false,
    annual_fee: false,
    ignore: false,
    product_changed: false,
    bonus_received: false,
});

const selectedSubDetails = ref({
    bonus_amount: false,
    minimum_spend_amount: false,
    minimum_spend_deadline: false,
    original_card: false,
    product_changed_date_opened: false,
    product_changed_date: false,
    product_changed_bonus_amount: false,
    product_changed_min_spend: false,
});

const subheaderText = computed(() =>
    currentStep.value === 3
        ? 'Review and confirm'
        : 'Select details to copy. New properties will be copied, existing ones will be overridden.'
);

const copyableDetails = computed(() => {
    if (!selectedCard.value?.user_managed_annual_fee) {
        return COPYABLE_DETAILS.filter((d) => d.key !== 'annual_fee');
    } else {
        return COPYABLE_DETAILS;
    }
});
const welcomeBonusDetails = computed(() => WELCOME_BONUS_DETAILS);
const productChangedDetails = computed(() => PRODUCT_CHANGED_DETAILS);

const detailsToCopy = computed(() => {
    return [
        ...copyableDetails.value.filter(
            (detail) => selectedDetails.value[detail.key]
        ),
        ...welcomeBonusDetails.value.filter(
            (detail) => selectedSubDetails.value[detail.key]
        ),
        ...productChangedDetails.value.filter(
            (detail) => selectedSubDetails.value[detail.key]
        ),
    ];
});

const canContinue = computed(() => {
    errors.value = {
        receivingCard: [],
        selectedCard: [],
        confirmChanges: [],
        madeSelections: [],
    };

    if (currentStep.value === 1 || currentStep.value === 2) {
        if (!selectedCard.value) {
            errors.value.selectedCard = [
                { message: 'Select a card to continue' },
            ];
            return false;
        }
        if (!selectedReceivingCard.value) {
            errors.value.receivingCard = [
                { message: 'Select a card to continue' },
            ];
            return false;
        }
    }

    if (currentStep.value === 2) {
        if (!Object.values(selectedDetails.value).some((val) => val === true)) {
            errors.value.madeSelections = [
                { message: 'Select at least one detail to continue' },
            ];
            return false;
        }
    }

    if (currentStep.value === 3 && !confirmChanges.value) {
        errors.value.confirmChanges = [{ message: 'Confirm to continue' }];
        return false;
    }

    return true;
});

const nextStep = async () => {
    if (!canContinue.value) return;

    if (currentStep.value === 3) {
        await copyDetails();
    } else {
        currentStep.value++;
    }
};

const setReceivingCard = ({ account }) => {
    if (!account) {
        selectedReceivingCard.value = null;
        return;
    }

    selectedReceivingCard.value = account;
};

const setCardToCopy = (card) => {
    if (!card) {
        selectedCard.value = null;
        setSelectedCard(null);
        return;
    }

    setSelectedCard(card.account);
};

const toggleSelectAll = () => {
    errors.value.madeSelections = [];
    Object.keys(selectedDetails.value).forEach((key) => {
        selectedDetails.value[key] = selectAll.value;
    });
    if (selectAll.value && selectedDetails.value.bonus_received) {
        Object.keys(welcomeBonusDetails.value).forEach((key) => {
            selectedSubDetails.value[key] = true;
        });
    }
};

const onDetailToggled = () => {
    errors.value.madeSelections = [];

    selectAll.value = Object.values(selectedDetails.value).every(
        (val) => val === true
    );
};

const isSelected = (key) => {
    return detailsToCopy.value.some((d) => d.key === key);
};

const prevStepOrCancel = () => {
    if (currentStep.value === 2) {
        resetValues();
    }
    if (currentStep.value > 1) {
        currentStep.value--;
    } else {
        closeModal();
    }
};

const formatCardDetails = (val, dataType) => {
    if (val === null || val === undefined) {
        return 'N/A';
    }

    if (typeof val === 'boolean') {
        return val ? 'Yes' : 'No';
    }

    if (
        dataType === 'credit_limit' ||
        dataType === 'annual_fee' ||
        dataType === 'minimum_spend_amount' ||
        dataType === 'product_changed_min_spend'
    ) {
        if (val === 0) {
            return '$0';
        }
        return `$${Filters.formatThousand(Filters.formatMoney(val).replace('$', ''))}`;
    }

    if (
        dataType === 'bonus_amount' ||
        dataType === 'product_changed_bonus_amount'
    ) {
        if (val === 0) {
            return '0';
        }
        return `${Filters.formatThousand(val)}`;
    }

    if (typeof val === 'string' && dataType.includes('date')) {
        return new Date(val).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }
    if (typeof val === 'string') {
        return Filters.ucWords(val);
    }

    if (val instanceof Date) {
        return val.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }

    return val;
};

const copyDetails = async () => {
    try {
        const cardToUpdate = { ...selectedReceivingCard.value };

        detailsToCopy.value.forEach((detail) => {
            cardToUpdate[detail.key] = selectedCard.value[detail.key];

            if (
                detail.key === 'product_changed' &&
                selectedCard.value['product_changed'] === true
            ) {
                cardToUpdate.original_card_id =
                    selectedCard.value.original_card_id;
            }

            if (detail.key === 'annual_fee') {
                cardToUpdate.user_managed_annual_fee = true;
            }
        });

        await updateCard(cardToUpdate);

        notify(
            {
                group: 'success',
                title: 'Success',
                text: 'Card details copied successfully.',
            },
            4000
        );
        closeModal();
    } catch (error) {
        console.error('Error updating card details:', error);

        notify(
            {
                group: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    'Failed to copy card details.',
            },
            4000
        );
    }
};

const closeModal = () => {
    emit('close');
    currentStep.value = 1;
    setSelectedCard(null);
    resetValues();
};

const resetValues = () => {
    selectedReceivingCard.value = null;
    selectAll.value = false;
    confirmChanges.value = false;
    Object.keys(selectedDetails.value).forEach((key) => {
        selectedDetails.value[key] = false;
    });
    Object.keys(selectedSubDetails.value).forEach((key) => {
        selectedSubDetails.value[key] = false;
    });
};

watch([() => selectedCard.value, () => selectedReceivingCard.value], () => {
    if (selectedCard.value) {
        errors.value.selectedCard = [];
    }
    if (selectedReceivingCard.value) {
        errors.value.selectedReceivingCard = [];
    }
    if (
        currentStep.value === 1 &&
        selectedCard.value &&
        selectedReceivingCard.value
    ) {
        nextStep();
    }
});

watch(
    [
        () => selectedDetails.value.bonus_received,
        () => selectedDetails.value.product_changed,
    ],
    ([bonus_received, product_changed]) => {
        selectedSubDetails.value.bonus_amount = bonus_received;
        selectedSubDetails.value.minimum_spend_amount = bonus_received;
        selectedSubDetails.value.minimum_spend_deadline = bonus_received;
        selectedSubDetails.value.original_card = product_changed;
        selectedSubDetails.value.product_changed_date_opened = product_changed;
        selectedSubDetails.value.product_changed_date = product_changed;
        selectedSubDetails.value.product_changed_bonus_amount = product_changed;
        selectedSubDetails.value.product_changed_min_spend = product_changed;
    },
    { immediate: true }
);

watch(
    errors,
    (newErrors) => {
        const hasErrors = Object.values(newErrors).some(
            (errorArray) => errorArray.length > 0
        );

        if (hasErrors) {
            nextTick(() => {
                const firstError = document.querySelector('.error-message');
                if (firstError) {
                    firstError.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            });
        }
    },
    { deep: true }
);
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.expand-enter-active,
.expand-leave-active {
    transition: all 0.3s ease-out;
    max-height: 1000px;
}

.expand-enter-from,
.expand-leave-to {
    opacity: 0;
    max-height: 0;
}

.sub-details {
    @apply flex flex-col gap-2;
}

.list-enter-active,
.list-leave-active {
    transition: all 0.3s ease-in-out;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

.list-enter-to,
.list-leave-from {
    opacity: 1;
    transform: translateX(0);
}

.list-move {
    transition: transform 0.3s ease-in-out;
}
</style>
